import React, { Fragment, useEffect, useRef, useState } from 'react';

// packages
import 'react-phone-input-2/lib/style.css';
import PhoneInput from 'react-phone-input-2';
import classnames from 'classnames';
import PropTypes from 'prop-types';

// styles
import inputStyles from '../styles.module.scss';
import '../input.scss';

// helpers
import { userEntersNumber } from '../../../helpers/utils/formValidations';

const PhoneNumberInput = ({
  error,
  name,
  defaultCountryCode = '91',
  handleCountryCode,
  onChange,
  value,
  ...restProps
}) => {
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const [focused, setFocused] = useState(false);
  const [dropdownWidth, setDropdownWidth] = useState('100%');

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setDropdownWidth(`${containerRef.current.offsetWidth}px`);
      }
    };

    updateWidth(); // Initial calculation
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const handleInputChange = (e) => {
    let numericValue = e?.target?.value?.replace(/[^0-9]/g, '') || ''
    if (onChange) {
      onChange({
        ...e,
        target: {
          name: e?.target?.name,
          value: numericValue,
        },
        currentTarget: {
          name: e?.target?.name,
          value: numericValue,
        }
      });
    }
  };

  const handleKeyDown = (e) => {
    if (['e', 'E', '-', '.', '+', 'Unidentified'].includes(e.key)) {
      e.preventDefault();
      e?.stopPropagation();
    }
  };

  return (
    <Fragment>
      <div
        ref={containerRef}
        className={classnames(
          inputStyles['input_field'],
          error && inputStyles['danger'],
          (focused || inputRef?.current?.value) && !error && inputStyles['focused'],
        )}
        style={{ opacity: !!restProps?.disabled ? '0.5' : '1' }}
      >
        <Fragment>
          <div className={classnames(inputStyles['phone-input-wrapper'])}>
            <p className={classnames(inputStyles['iso-text'])}>{`+${defaultCountryCode} `}</p>
            <PhoneInput
              value={defaultCountryCode}
              enableSearch
              onChange={(_, data) => {
                handleCountryCode(data?.dialCode);
              }}
              dropdownStyle={{
                width: dropdownWidth,
              }}
              disabled={!!restProps?.disabled}
            />
          </div>
        </Fragment>
        {restProps?.placeholder && (
          <p
            className={classnames(
              inputStyles['label'],
              focused || inputRef?.current?.value || restProps?.value
                ? inputStyles['label_active']
                : inputStyles['label_none'],
            )}
          >
            {restProps?.placeholder}
          </p>
        )}
        <div
          onClick={() => {
            inputRef?.current?.focus();
          }}
          style={{ width: '100%' }}
        >
          <input
            ref={inputRef}
            name={name}
            placeholder={!focused ? restProps?.placeholder : ''}
            onFocus={(e) => {
              restProps?.onFocus && restProps.onFocus(e);
              setFocused(true);
            }}
            onBlur={(e) => {
              restProps?.onBlur && restProps?.onBlur(e);
              setFocused(false);
            }}
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            onWheel={(event) => event.currentTarget.blur()}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            value={value || ''}
            disabled={!!restProps?.disabled}
          />
        </div>
      </div>
      {error && <p className={inputStyles['error']}>{error}</p>}
    </Fragment>
  );
};

PhoneNumberInput.propTypes = {
  defaultCountryCode: PropTypes.string,
  name: PropTypes.string.isRequired,
  error: PropTypes.string,
  handleCountryCode: PropTypes.func.isRequired,
  onChange: PropTypes.func,
};

export default React.memo(PhoneNumberInput);